import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Portal, useEscapeKey, useScrollLock } from '@/ui/kit'
import { cn } from './internal/utils'

const Preview = ({ src, alt, onClose }) => {
  const { t } = useTranslation()
  useEscapeKey(onClose)
  useScrollLock(true)

  return (
    <Portal>
      <div
        className="fixed inset-0 flex items-center justify-center bg-black/80 p-6"
        style={{ zIndex: 'var(--z-modal, 2000)' }}
        role="dialog"
        aria-modal="true"
        aria-label={alt || t('common.imagePreview')}
        onClick={onClose}
      >
        <img src={src} alt={alt} className="max-h-full max-w-full object-contain" />
        <button
          type="button"
          aria-label={t('common.closePreview')}
          onClick={onClose}
          className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
        >
          <X size={20} aria-hidden="true" />
        </button>
      </div>
    </Portal>
  )
}

/**
 * antd Image. Supports `preview` (boolean or `{ src, visible, onVisibleChange }`),
 * `fallback` and `placeholder`.
 */
const Image = ({
  src,
  alt = '',
  width,
  height,
  preview = true,
  fallback,
  placeholder,
  rootClassName,
  className,
  style,
  onError,
  ...rest
}) => {
  const previewConfig = typeof preview === 'object' && preview !== null ? preview : {}
  const previewEnabled = preview !== false
  const [failed, setFailed] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [open, setOpen] = useState(Boolean(previewConfig.visible))

  useEffect(() => {
    if (previewConfig.visible !== undefined) setOpen(previewConfig.visible)
  }, [previewConfig.visible])

  const setVisible = (next) => {
    if (previewConfig.visible === undefined) setOpen(next)
    previewConfig.onVisibleChange?.(next, !next)
  }

  const displaySrc = failed && fallback ? fallback : src

  return (
    <div className={cn('ui-image relative inline-block', rootClassName)} style={{ width, height }}>
      {!loaded && placeholder ? (
        <div className="ui-image-placeholder absolute inset-0">{placeholder}</div>
      ) : null}
      <img
        src={displaySrc}
        alt={alt}
        width={width}
        height={height}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={(event) => {
          setFailed(true)
          onError?.(event)
        }}
        onClick={previewEnabled ? () => setVisible(true) : undefined}
        className={cn(
          'ui-image-img h-full w-full object-cover',
          previewEnabled && 'cursor-zoom-in',
          className
        )}
        style={style}
        {...rest}
      />
      {open && previewEnabled ? (
        <Preview src={previewConfig.src || displaySrc} alt={alt} onClose={() => setVisible(false)} />
      ) : null}
    </div>
  )
}

const PreviewGroup = ({ children }) => <div className="ui-image-preview-group">{children}</div>

Image.PreviewGroup = PreviewGroup

export default Image
